import React, { useEffect, useState } from "react";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import BdrMenu from "../../../../components/BdrMenu/BdrMenu";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import { useDriveTimePins } from "../../../../utils/DriveTimePin/DriveTimePinContext";
import { hasAddress, samePlace } from "../../../../utils/DriveTimePin/driveTimeFormat";
import DriveTimeModal from "./DriveTimeModal";

/**
 * The one drive-time control every address surface gets (2026-10-09).
 *
 * ONE BUTTON, A SMALL MENU. The first design was a separate pin action and a
 * separate compare action per surface — two more icons on rows that are
 * already dense (the client rail's quick-action row, an Available Sooner row).
 * A menu keeps it to one control and reads the state out loud: it says what
 * is pinned, offers "drive time from the pins to here" when that makes sense,
 * and offers pinning this address as start or end.
 *
 * `place` = `{ label, address }` from `makePlace`. Disabled when the place has
 * no address at all (nothing to route).
 */
const DriveTimeButton = ({
  place,
  variant = "action",
  size,
  iconOnly = true,
  className = "",
  title = "Drive time",
  /** the host's overlay rung — `raised` from inside the calendar's event popup */
  layer = "base",
  /** menu nesting; the popup is not a BdrMenu so 0 is right everywhere today */
  menuDepth = 0,
  /**
   * Walkthrough hooks (registry §17.10). `openRef`: a ref the host can call
   * to open the compare dialog from a tour action (`openRef.current()`).
   * `demoLegs`: canned legs for the dialog — no Google lookup.
   */
  openRef,
  demoLegs,
}) => {
  const pins = useDriveTimePins();
  const { handleSnack } = useSnackbar();
  const [anchorEl, setAnchorEl] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (!openRef) return undefined;
    openRef.current = () => setModalOpen(true);
    return () => {
      openRef.current = null;
    };
  }, [openRef]);

  const routable = hasAddress(place?.address);
  const isStart = samePlace(pins.start, place);
  const isEnd = samePlace(pins.end, place);
  /* Comparing this place against a pin that IS this place is a zero-length
     leg; only offer the compare when at least one pin is somewhere else. */
  const canCompare = (!!pins.start && !isStart) || (!!pins.end && !isEnd);

  const close = () => setAnchorEl(null);

  const pinSummary = pins.hasAny
    ? `${pins.start ? pins.start.label : "no start"} → ${pins.end ? pins.end.label : "no end"}`
    : "Nothing pinned yet";

  const items = [
    { key: "summary", header: true, label: pinSummary },
    {
      key: "compare",
      label: "Drive time from the pins to here",
      disabled: !canCompare,
      onClick: () => {
        close();
        setModalOpen(true);
      },
    },
    { key: "d1", divider: true },
    {
      key: "pin-start",
      label: isStart ? "Pinned as start" : "Pin as start",
      disabled: isStart,
      onClick: () => {
        pins.pinStart(place);
        handleSnack(`Pinned as start: ${place.label}`, 2000);
        close();
      },
    },
    {
      key: "pin-end",
      label: isEnd ? "Pinned as end" : "Pin as end",
      disabled: isEnd,
      onClick: () => {
        pins.pinEnd(place);
        handleSnack(`Pinned as end: ${place.label}`, 2000);
        close();
      },
    },
    pins.hasAny && { key: "d2", divider: true },
    pins.hasAny && {
      key: "clear",
      label: "Clear pins",
      danger: true,
      onClick: () => {
        pins.clearAll();
        close();
      },
    },
  ];

  return (
    <>
      <BdrButton
        variant={variant}
        size={size}
        iconOnly={iconOnly}
        icon={<AltRouteIcon />}
        ariaLabel="Drive time"
        title={title}
        className={className}
        disabled={!routable}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        {iconOnly ? null : "Drive time"}
      </BdrButton>
      <BdrMenu
        anchorEl={anchorEl}
        onClose={close}
        ariaLabel="Drive time"
        depth={menuDepth}
        items={items}
      />
      <DriveTimeModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        target={place}
        layer={layer}
        demoLegs={demoLegs}
      />
    </>
  );
};

export default DriveTimeButton;
